import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HeartPulse, Loader2, X } from "lucide-react";

const BUCKETS = [["submitted", "Pending"], ["under_review", "Under Review"], ["approved", "Approved"],
  ["info_requested", "Info Requested"], ["rejected", "Rejected"], ["suspended", "Suspended"],
  ["deactivated", "Deactivated"], ["draft", "Drafts"]];

export default function AdminTelehealth() {
  const [bucket, setBucket] = useState("submitted");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [expirations, setExpirations] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/telehealth/providers?status=${bucket}`); setRows(data); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, [bucket]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/telehealth/expirations").then(({ data }) => setExpirations(data)).catch(() => {}); }, []);

  const open = async (pid) => {
    const { data } = await api.get(`/admin/telehealth/providers/${pid}`);
    setDetail(data);
  };

  const act = async (pid, action, needsReason = false, promptText = "Administrative reason (required):") => {
    let reason = "";
    if (needsReason) { reason = window.prompt(promptText) || ""; if (!reason.trim()) { toast.error("A reason is required."); return; } }
    try {
      await api.post(`/admin/telehealth/providers/${pid}/action`, { action, reason });
      toast.success(`Action recorded: ${action}`);
      setDetail(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-telehealth">
      <div className="flex items-center gap-2">
        <HeartPulse className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">Telehealth Providers</h3>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {BUCKETS.map(([k, label]) => (
          <button key={k} onClick={() => setBucket(k)} data-testid={`th-bucket-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap ${bucket === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
        rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="th-admin-empty">No providers in this bucket.</div> :
        <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-50">
          {rows.map((p) => (
            <button key={p.provider_id} onClick={() => open(p.provider_id)} data-testid={`th-provider-row-${p.provider_id}`}
              className="w-full text-left p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50">
              <div>
                <div className="font-semibold text-navy text-sm">{p.org?.practice_name || p.org?.legal_name || "Unnamed practice"}</div>
                <div className="text-xs text-slate-500">{(p.clinical?.provider_type || "—").replace(/_/g, " ")} · {(p.coverage?.states || []).join(", ") || "no states"} · {p.contact?.email}</div>
              </div>
              <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">{p.status_label}</span>
            </button>
          ))}
        </div>}

      {expirations.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 p-5" data-testid="th-expirations">
          <h4 className="font-heading font-semibold text-navy text-sm mb-2">Credential expirations</h4>
          {expirations.slice(0, 10).map((e, i) => (
            <div key={i} className={`text-xs py-1 flex justify-between ${e.expired ? "text-red-500" : "text-slate-500"}`}>
              <span>{e.practice_name} — {e.name} ({e.credential_type})</span><span>{e.expires_at}{e.expired ? " · EXPIRED" : ""}</span>
            </div>
          ))}
        </div>
      )}

      <EscalationsPanel />

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="th-review-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-navy">{detail.org?.practice_name || detail.org?.legal_name}</h3>
              <button onClick={() => setDetail(null)} data-testid="th-review-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="mt-1 text-xs text-slate-500">{detail.status_label} · Reviewer: {detail.reviewer || "unassigned"}</div>

            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-xs text-slate-600">
              <div className="rounded-2xl border border-slate-100 p-3">
                <div className="font-semibold text-navy mb-1">Organization</div>
                <div>Legal: {detail.org?.legal_name || "—"}</div><div>Type: {detail.org?.org_type || "—"}</div>
                <div>Address: {detail.org?.address || "—"}</div><div>Phone: {detail.org?.main_phone || "—"}</div>
                <div>Email: {detail.org?.general_email || "—"}</div><div>Tax ID: •••{detail.org?.tax_id_last4 || "—"}</div>
              </div>
              <div className="rounded-2xl border border-slate-100 p-3">
                <div className="font-semibold text-navy mb-1">Contact & Coverage</div>
                <div>{detail.contact?.name} — {detail.contact?.title || "contact"}</div>
                <div>{detail.contact?.email} · {detail.contact?.mobile || "no mobile"}</div>
                <div className="mt-1">States: {(detail.coverage?.states || []).join(", ") || "—"}</div>
                <div>Provider type: {(detail.clinical?.provider_type || "—").replace(/_/g, " ")}</div>
              </div>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-100 p-3 text-xs">
              <div className="font-semibold text-navy mb-1">Provider roster & licenses</div>
              {(detail.clinical_full?.roster || []).length === 0 ? <span className="text-slate-400">No licensed providers listed.</span> :
                detail.clinical_full.roster.map((r, i) => <div key={i}>{r.name} — {r.credential_type} · {r.license_number} ({r.license_state}) · exp {r.expires_at || "—"}{r.npi ? ` · NPI ${r.npi}` : ""}</div>)}
              <div className="mt-1 text-slate-500">Liability: {detail.clinical_full?.liability_insurance?.carrier || "—"} exp {detail.clinical_full?.liability_insurance?.policy_expires || "—"}</div>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-100 p-3 text-xs">
              <div className="font-semibold text-navy mb-1">Documents</div>
              {Object.keys(detail.documents || {}).length === 0 ? <span className="text-slate-400">No documents uploaded.</span> :
                Object.entries(detail.documents).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5">
                    <span>{k.replace(/_/g, " ")} — {v.filename}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/telehealth/providers/${detail.provider_id}/documents/${k}`} target="_blank" rel="noreferrer" className="text-cobalt">View</a>
                  </div>
                ))}
            </div>

            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded-2xl border border-slate-100 p-3 max-h-40 overflow-y-auto">
                <div className="font-semibold text-navy mb-1">Timeline</div>
                {(detail.timeline || []).map((t, i) => <div key={i} className="py-0.5">{t.event}{t.reason ? ` — ${t.reason}` : ""} <span className="text-slate-400">{new Date(t.at).toLocaleString()}</span></div>)}
              </div>
              <div className="rounded-2xl border border-slate-100 p-3 max-h-40 overflow-y-auto">
                <div className="font-semibold text-navy mb-1">Audit trail</div>
                {(detail.audit || []).map((a) => <div key={a.audit_id} className="py-0.5">{a.event} <span className="text-slate-400">{new Date(a.at).toLocaleString()}</span></div>)}
              </div>
            </div>

            {(detail.internal_notes || []).length > 0 && (
              <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-xs">
                <div className="font-semibold text-navy mb-1">Internal notes</div>
                {detail.internal_notes.map((n, i) => <div key={i}>“{n.text}” — {n.by}</div>)}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {detail.status !== "approved" && <button onClick={() => act(detail.provider_id, "approve")} data-testid="th-act-approve" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold">Approve</button>}
              {detail.status === "submitted" && <button onClick={() => act(detail.provider_id, "start_review")} data-testid="th-act-review" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold">Start review</button>}
              <button onClick={() => act(detail.provider_id, "request_info", true, "Describe the additional information required:")} data-testid="th-act-info" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">Request info</button>
              {detail.status !== "rejected" && <button onClick={() => act(detail.provider_id, "reject", true)} data-testid="th-act-reject" className="px-4 py-2 rounded-full bg-white border border-red-200 text-red-500 text-xs">Reject</button>}
              {detail.status === "approved" && <button onClick={() => act(detail.provider_id, "suspend", true)} data-testid="th-act-suspend" className="px-4 py-2 rounded-full bg-white border border-red-200 text-red-500 text-xs">Suspend</button>}
              {detail.status === "suspended" && <button onClick={() => act(detail.provider_id, "reactivate")} data-testid="th-act-reactivate" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">Reactivate</button>}
              {detail.status !== "deactivated" && <button onClick={() => act(detail.provider_id, "deactivate", true)} data-testid="th-act-deactivate" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-500 text-xs">Deactivate</button>}
              <button onClick={() => act(detail.provider_id, "note", true, "Internal note:")} data-testid="th-act-note" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">Add note</button>
              <button onClick={() => act(detail.provider_id, "assign_reviewer", true, "Reviewer name:")} data-testid="th-act-reviewer" className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">Assign reviewer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EscalationsPanel = () => {
  const [rows, setRows] = useState(null);
  const [cfg, setCfg] = useState(null);
  const load = () => {
    api.get("/admin/telehealth/escalations").then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get("/admin/telehealth/escalation-config").then(({ data }) => setCfg(data)).catch(() => {});
  };
  useEffect(load, []);
  const clear = async (id) => {
    const reason = window.prompt("Clearing reason from the reviewing clinician (required, permanently logged):");
    if (!reason) return;
    try { await api.post(`/admin/telehealth/escalations/${id}/clear`, { reason }); toast.success("Escalation cleared — case may re-enter routine coordination"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const toggleKill = async () => {
    try {
      const { data } = await api.put("/admin/telehealth/escalation-config", { automation_disabled: !cfg.automation_disabled });
      setCfg(data); toast.success(data.automation_disabled ? "Telehealth automation DISABLED platform-wide" : "Telehealth automation re-enabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-5" data-testid="th-escalations">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h4 className="font-heading font-semibold text-navy text-sm">Safety escalations & automation control</h4>
        {cfg && (
          <button onClick={toggleKill} data-testid="th-kill-switch"
            className={`px-4 py-1.5 rounded-full text-xs font-bold ${cfg.automation_disabled ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600"}`}>
            {cfg.automation_disabled ? "AUTOMATION DISABLED — click to re-enable" : "Disable all telehealth automation"}
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Flags are workflow alerts based on client/RN-entered information — never a medical conclusion. Flagged cases are held from routine scheduling until an authorized clinician clears them.</p>
      {rows === null ? <div className="py-4 text-center text-xs text-slate-400">Loading…</div> :
        rows.length === 0 ? <div className="py-4 text-center text-xs text-slate-400" data-testid="th-escalations-empty">No pending safety escalations.</div> :
        rows.map((e) => (
          <div key={e.escalation_id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-50 last:border-0 text-sm" data-testid={`th-escalation-${e.escalation_id}`}>
            <div>
              <span className="font-mono text-xs text-slate-400">{e.case_number || e.case_id}</span>
              <span className="ml-2 text-red-600 text-xs font-semibold">{(e.signals || []).join(", ")}</span>
            </div>
            <button onClick={() => clear(e.escalation_id)} data-testid={`th-clear-${e.escalation_id}`}
              className="px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold">Clinician clear</button>
          </div>
        ))}
    </div>
  );
};
